<template>
    <div class="user-roles">
        <el-alert class="user-roles__tip" title="超级管理员（sa）可在此处对用户进行权限设置" type="success" :closable="false" />

        <el-card class="panel-card" shadow="never">
            <template #header>
                <div class="panel-card__head">
                    <span class="panel-card__title">用户列表</span>
                    <div class="panel-card__tools">
                        <el-input v-model="keyword" class="user-search" placeholder="搜索姓名 / 账号 / 邮箱" clearable>
                            <template #prefix>
                                <el-icon><Search /></el-icon>
                            </template>
                        </el-input>
                        <span class="count-pill">共 {{ userRows.length }} 人</span>
                    </div>
                </div>
            </template>

            <el-table class="user-table" :data="userRows" stripe style="width: 100%">
                <el-table-column label="用户" min-width="220">
                    <template #default="scope">
                        <div class="user-cell">
                            <el-avatar :size="36" :src="currentAvatar(scope.row.avatar)" class="user-cell__avatar" />
                            <div class="user-cell__main">
                                <div class="user-cell__name">
                                    <span>{{ scope.row.realName || '未命名' }}</span>
                                    <el-tag v-if="scope.row.isOnline" type="success" size="small" effect="plain">在线</el-tag>
                                    <el-tag v-if="scope.row.isFrozen" type="danger" size="small" effect="plain">已冻结</el-tag>
                                </div>
                                <div class="user-cell__sub">{{ scope.row.name }}</div>
                            </div>
                        </div>
                    </template>
                </el-table-column>

                <el-table-column label="邮箱" min-width="240">
                    <template #default="scope">
                        <div class="user-cell__main">
                            <span>{{ scope.row.email }}</span>
                            <span class="user-cell__sub">注册于：{{ scope.row.createTime }}</span>
                        </div>
                    </template>
                </el-table-column>

                <el-table-column label="角色" width="150">
                    <template #default="scope">
                        <template v-if="roleList(scope.row).length">
                            <el-tag v-for="role in roleList(scope.row)" :key="role" :type="roleTagType(role)" size="small" effect="light" class="role-tag">
                                {{ roleLabel(role) }}
                            </el-tag>
                        </template>
                        <span v-else class="user-cell__sub">普通用户</span>
                    </template>
                </el-table-column>

                <el-table-column label="空间用量" width="190">
                    <template #default="scope">
                        <div class="space-cell">
                            <div class="space-cell__text">
                                <span>{{ spaceInfo(scope.row).used }} / {{ spaceInfo(scope.row).total }} MB</span>
                                <span>{{ spaceInfo(scope.row).percent }}%</span>
                            </div>
                            <el-progress :percentage="spaceInfo(scope.row).percent" :stroke-width="6" :show-text="false" :color="spaceColor(scope.row)" />
                        </div>
                    </template>
                </el-table-column>

                <el-table-column label="最近登录" width="120">
                    <template #default="scope">
                        <span>{{ setDatetimeDiff(scope.row.loginTime) || '—' }}</span>
                    </template>
                </el-table-column>

                <el-table-column label="操作" width="110" fixed="right">
                    <template #default="scope">
                        <el-dropdown trigger="click" @command="command => handleCommand(command, scope.row)">
                            <el-button class="op-btn" text>
                                操作
                                <el-icon class="op-btn__icon"><ArrowDown /></el-icon>
                            </el-button>
                            <template #dropdown>
                                <el-dropdown-menu>
                                    <el-dropdown-item command="setAdmin" :disabled="isAdmin(scope.row)">
                                        <el-icon><Key /></el-icon>
                                        <span>设置管理员</span>
                                    </el-dropdown-item>
                                    <el-dropdown-item command="removeAdmin" :disabled="!isAdmin(scope.row)">
                                        <el-icon><Key /></el-icon>
                                        <span>撤销管理员</span>
                                    </el-dropdown-item>
                                    <el-dropdown-item command="freeze" divided :disabled="scope.row.isFrozen">
                                        <el-icon><Lock /></el-icon>
                                        <span>冻结账号</span>
                                    </el-dropdown-item>
                                    <el-dropdown-item command="unfreeze" :disabled="!scope.row.isFrozen">
                                        <el-icon><Unlock /></el-icon>
                                        <span>恢复账号</span>
                                    </el-dropdown-item>
                                    <el-dropdown-item command="expand" divided>
                                        <el-icon><Plus /></el-icon>
                                        <span>扩容 100MB</span>
                                    </el-dropdown-item>
                                    <el-dropdown-item command="shrink">
                                        <el-icon><Minus /></el-icon>
                                        <span>缩容 100MB</span>
                                    </el-dropdown-item>
                                </el-dropdown-menu>
                            </template>
                        </el-dropdown>
                    </template>
                </el-table-column>
            </el-table>
        </el-card>
    </div>
</template>

<script>
import { ElMessage, ElMessageBox } from 'element-plus';
import request from '../../../utils/request.js';
import { avatarImage } from '../../../utils/users.js';
import { datetimeDiff } from '../../../utils/datetime.js';

export default {
    data() {
        return {
            userList: [],
            keyword: ''
        };
    },
    computed: {
        // 本地关键字过滤：姓名 / 账号 / 邮箱，便于在用户较多时快速定位
        userRows() {
            const keyword = this.keyword.trim().toLowerCase();
            if (!keyword) return this.userList;
            return this.userList.filter(row => [row.realName, row.name, row.email].some(value => (value || '').toLowerCase().includes(keyword)));
        }
    },
    mounted() {
        this.loadUserList();
    },
    components: {},
    methods: {
        setDatetimeDiff(dt) {
            return datetimeDiff(dt);
        },
        currentAvatar(createUserAvatar) {
            return avatarImage(createUserAvatar);
        },
        userLabel(row) {
            return row.realName || row.name || row.email || '该用户';
        },
        // 角色字段兼容数组与逗号分隔字符串两种返回形式
        roleList(row) {
            if (Array.isArray(row.roles)) return row.roles.filter(Boolean);
            if (typeof row.roles === 'string' && row.roles.length > 0) return row.roles.split(',').filter(Boolean);
            return [];
        },
        isAdmin(row) {
            return this.roleList(row).includes('admin');
        },
        roleLabel(role) {
            if (role == 'sa') return '超级管理员';
            if (role == 'admin') return '管理员';
            return role;
        },
        roleTagType(role) {
            if (role == 'sa') return 'danger';
            if (role == 'admin') return 'warning';
            return 'info';
        },
        spaceInfo(row) {
            const conf = row.sysUserFileConf;
            if (!conf) return { used: 0, total: 0, percent: 0 };
            let percent = Number(conf.usedSpacePercent);
            if (!Number.isFinite(percent)) percent = 0;
            percent = Math.max(0, Math.min(100, Math.round(percent)));
            return {
                used: parseInt((conf.usedSpace || 0) / 1024 / 1024),
                total: parseInt((conf.spaceLimit || 0) / 1024 / 1024),
                percent: percent
            };
        },
        // 用量接近上限时用颜色提醒
        spaceColor(row) {
            const percent = this.spaceInfo(row).percent;
            if (percent >= 90) return '#f56c6c';
            if (percent >= 70) return '#e6a23c';
            return '#2f6fed';
        },
        // 危险操作先确认，避免误触
        confirmThen(message, action) {
            ElMessageBox.confirm(message, '操作确认', {
                type: 'warning',
                confirmButtonText: '确定',
                cancelButtonText: '取消'
            })
                .then(() => action())
                .catch(() => {
                    // 用户取消，无需处理
                });
        },
        handleCommand(command, row) {
            switch (command) {
                case 'setAdmin':
                    this.confirmThen(`确定将「${this.userLabel(row)}」设置为管理员吗？`, () => this.setAdmin(row, true));
                    break;
                case 'removeAdmin':
                    this.confirmThen(`确定撤销「${this.userLabel(row)}」的管理员权限吗？`, () => this.setAdmin(row, false));
                    break;
                case 'freeze':
                    this.confirmThen(`冻结后「${this.userLabel(row)}」将无法登录，确定冻结吗？`, () => this.setFrozen(row, true));
                    break;
                case 'unfreeze':
                    this.setFrozen(row, false);
                    break;
                case 'expand':
                    this.extendFileSpace(row, true);
                    break;
                case 'shrink':
                    this.confirmThen(`确定将「${this.userLabel(row)}」的空间容量缩减 100MB 吗？`, () => this.extendFileSpace(row, false));
                    break;
            }
        },
        loadUserList() {
            return request({
                url: '/user/getUsers',
                method: 'post',
                data: { token: this.$store.state.user.token }
            }).then(res => {
                if (res.code == 0) {
                    this.userList = res.data;
                }
            });
        },
        setAdmin(row, op) {
            return request({
                url: '/user/setAdmin',
                method: 'post',
                data: { token: this.$store.state.user.token, userId: row.id, op: op }
            }).then(res => {
                if (res.code == 0) {
                    this.onOperateSuccess(res);
                }
            });
        },
        extendFileSpace(row, op) {
            return request({
                url: '/user/extendFileSpace',
                method: 'post',
                data: { token: this.$store.state.user.token, userId: row.id, op: op }
            }).then(res => {
                if (res.code == 0) {
                    this.onOperateSuccess(res);
                }
            });
        },
        setFrozen(row, op) {
            return request({
                url: '/user/setFrozen',
                method: 'post',
                data: { token: this.$store.state.user.token, userId: row.id, op: op }
            }).then(res => {
                if (res.code == 0) {
                    this.onOperateSuccess(res);
                }
            });
        },
        onOperateSuccess(res) {
            ElMessage.success((res && res.msg) || '操作成功');
            this.loadUserList();
        }
    }
};
</script>

<style scoped>
/* ===================== 用户权限管理 ===================== */
.user-roles {
    --ur-text: #1f2d3d;
    --ur-sub: #5b6a80;
    --ur-muted: #8a94a6;
    --ur-line: #eef0f4;

    /* 局部覆盖主色，保持与工作台其它页面一致 */
    --el-color-primary: #2f6fed;
    --el-color-primary-light-3: #6d9af2;
    --el-color-primary-light-5: #97b7f6;
    --el-color-primary-light-7: #c1d4fa;
    --el-color-primary-light-8: #d5e2fb;
    --el-color-primary-light-9: #eaf1fd;
    --el-color-primary-dark-2: #2659be;

    color: var(--ur-text);
}

/* ---------- 顶部说明 ---------- */
.user-roles .user-roles__tip {
    margin-bottom: 14px;
    border-radius: 10px;
}

/* ---------- 面板卡 ---------- */
.user-roles .panel-card {
    margin: 0;
    border: 1px solid var(--ur-line);
    border-radius: 12px;
}
.user-roles .panel-card :deep(.el-card__header) {
    padding: 14px 18px;
    border-bottom: 1px solid var(--ur-line);
}
.user-roles .panel-card :deep(.el-card__body) {
    padding: 16px 18px;
}
.panel-card__head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
}
.panel-card__title {
    position: relative;
    padding-left: 10px;
    font-size: 15px;
    font-weight: 600;
}
.panel-card__title::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 0;
    width: 3px;
    height: 14px;
    margin-top: -7px;
    border-radius: 2px;
    background: linear-gradient(180deg, #6a9cff, #3f6fff);
}
.panel-card__tools {
    display: flex;
    align-items: center;
    gap: 10px;
}
.user-roles .user-search {
    width: 220px;
}
.user-roles .user-search :deep(.el-input__wrapper) {
    border-radius: 9px;
}
.count-pill {
    flex: none;
    padding: 2px 8px;
    border-radius: 999px;
    background-color: var(--el-color-primary-light-9);
    color: var(--el-color-primary);
    font-size: 12px;
    font-weight: 500;
}

/* ---------- 表格 ---------- */
.user-roles :deep(.user-table) {
    --el-table-header-bg-color: #f7f9fc;
    font-size: 13px;
}
.user-roles :deep(.user-table th.el-table__cell) {
    color: var(--ur-muted);
    font-weight: 600;
}
.user-roles :deep(.user-table .el-table__cell) {
    padding: 8px 0;
}

/* ---------- 用户信息单元格 ---------- */
.user-cell {
    display: flex;
    align-items: center;
    gap: 10px;
}
.user-cell__avatar {
    flex: none;
    border: 1px solid var(--ur-line);
}
.user-cell__main {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
    line-height: 1.5;
}
.user-cell__name {
    display: flex;
    align-items: center;
    gap: 6px;
    font-weight: 500;
    color: var(--ur-text);
}
.user-cell__sub {
    font-size: 12px;
    color: var(--ur-muted);
    word-break: break-all;
}

/* ---------- 角色标签 ---------- */
.role-tag {
    margin: 2px 6px 2px 0;
}
.role-tag:last-child {
    margin-right: 0;
}

/* ---------- 空间用量 ---------- */
.space-cell {
    display: flex;
    flex-direction: column;
    gap: 4px;
}
.space-cell__text {
    display: flex;
    align-items: center;
    justify-content: space-between;
    font-size: 12px;
    color: var(--ur-sub);
}
.space-cell :deep(.el-progress-bar__outer) {
    border-radius: 999px;
}

/* ---------- 行内操作 ---------- */
.op-btn.el-button {
    padding: 0 8px;
    font-size: 13px;
    color: var(--el-color-primary);
}
.op-btn__icon {
    margin-left: 2px;
    font-size: 12px;
}

/* ---------- 小尺寸设备适配 ---------- */
@media (max-width: 768px) {
    .panel-card__head {
        flex-wrap: wrap;
    }
    .user-roles .user-search {
        width: 100%;
    }
    .panel-card__tools {
        flex: 1 1 auto;
        justify-content: space-between;
    }
    .user-roles .panel-card :deep(.el-card__header) {
        padding: 12px 14px;
    }
    .user-roles .panel-card :deep(.el-card__body) {
        padding: 12px;
    }
}
</style>
